import React, { useState, useEffect, useRef } from 'react';
import { useContext } from 'react';
import AuthContext from '../contexts/AuthContext';
import api from '../api';

const Notifications = ({ isOpen, onClose, notificationsRef }) => {
  const { user } = useContext(AuthContext);
  const panelRef = useRef(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  // Handle click outside for this component
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target) && 
          notificationsRef?.current && !notificationsRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, notificationsRef]);

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await api.get('/notifications');
      let userNotifications = response.data;
      if (user && !user.isPremium && user.subscriptionType !== 'premium') {
        // Free users see all but can't interact with premium
        userNotifications = userNotifications.map(notif => ({
          ...notif,
          isPremium: notif.type === 'premium',
          canInteract: false
        }));
      } else {
        userNotifications = response.data.map(notif => ({
          ...notif,
          isPremium: notif.type === 'premium',
          canInteract: true
        }));
      }
      setNotifications(userNotifications);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationClick = (notification) => {
    if (notification.link && user && (user.isPremium || user.subscriptionType === 'premium')) {
      window.open(notification.link, '_blank');
    } else if (notification.isPremium && (!user || user.subscriptionType !== 'premium')) {
      // Show upgrade message or something
      alert('This is a premium notification. Upgrade to premium to access the link.');
    }
  };

  const handleDelete = async (id) => {
    if (!user.isAdmin) return;
    if (!window.confirm('Are you sure you want to delete this notification?')) return;
    try {
      await api.delete(`/notifications/${id}`);
      fetchNotifications(); // Refetch to update list
    } catch (error) {
      alert('Failed to delete notification.');
      console.error(error);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      ref={panelRef}
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        width: '400px',
        height: '100vh',
        backgroundColor: 'white',
        boxShadow: '-4px 0 20px rgba(0,0,0,0.1)',
        zIndex: 1000,
        transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.3s ease',
        overflowY: 'auto'
      }}
    >
      <div style={{ padding: '1rem', borderBottom: '1px solid  #000000', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: 'black' }}>
        <h3>Notifications</h3>
        <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>×</button>
      </div>
      {loading ? (
        <p style={{ padding: '1rem', textAlign: 'center' , color: 'black' }}>Loading...</p>
      ) : (
        <div style={{ padding: '1rem' }}>
          {notifications.length === 0 ? (
            <p style={{ color: 'black', padding: '1rem', textAlign: 'center', color: 'black' }}>No notifications</p>
          ) : (
            notifications.map((notif) => (
              <div key={notif.id} style={{
                padding: '1rem',
                borderBottom: '1px solid #eee',
                cursor: notif.link && (user?.isPremium || user?.subscriptionType === 'premium') ? 'pointer' : 'default',
                opacity: notif.type === 'premium' && (!user || user.subscriptionType !== 'premium') ? 0.6 : 1
              }} onClick={() => handleNotificationClick(notif)}>
                <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1.1rem', fontWeight: 'bold', color: 'black' }}>{notif.title}</h4>
                <p style={{ margin: '0 0 0.5rem 0', color: 'black' }}>{notif.content}</p>
                <p style={{ margin: 0, fontSize: '0.9rem', color: 'black' }}>
                  {new Date(notif.date).toLocaleDateString()} {notif.type === 'premium' && <span style={{ color: '#8e44ad' }}>(Premium)</span>}
                </p>
                {notif.link && (user?.isPremium || user?.subscriptionType === 'premium') && (
                  <p style={{ margin: '0.5rem 0 0 0', color: '#007bff' }}>Click to open link</p>
                )}
                {user?.isAdmin && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(notif.id);
                    }}
                    style={{
                      marginTop: '0.5rem',
                      padding: '0.25rem 0.5rem',
                      backgroundColor: '#e74c3c',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.8rem'
                    }}
                  >
                    Delete
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default Notifications;
